import { useMemo } from "react";
import linkifyHtml from "linkifyjs/html";

import { convertMentionIntoHref } from "./convertMentionIntoHref";
import { UseReactHtmlReplace } from "./useReactHtmlReplace";
import { convertAnnouncementLinkIntoHref } from "./convertAnnouncementLinkIntoHref";
import { isRichPostContent } from "./richPostContent";
import { normalizePostWhitespace } from "./normalizePostWhitespace";
import { sanitizePostHtml } from "./sanitizePostHtml";
import { ILinkedCommunityDocuments } from "models";
import RichPostBody from "components/RichPostBody";

interface IUseShowDescriptionWithMentions {
	text: string;
	mentions?: string[];
	linked_community_documents?: ILinkedCommunityDocuments[];
	words?: number;
	maxHeight?: string;
	// Rich-text (HTML) post body (#1269): preserve formatting tags while still
	// converting mention tokens to clickable links. Legacy plain-text bodies
	// leave this false so behaviour is unchanged.
	rich?: boolean;
}

export const UseShowDescriptionWithMentions = (
	props: IUseShowDescriptionWithMentions,
) => {
	const {
		text,
		mentions = [],
		linked_community_documents = [],
		words = 60,
		maxHeight,
		rich: richOverride,
	} = props;
	const rich = richOverride ?? isRichPostContent(text);

	const clean: string = useMemo(() => {
		if (text) {
			// Strip any src-less `<img>` left behind by a failed upload
			// (e.g. `<p><img style="max-width:100%;height:auto"/></p>`) so it
			// doesn't render as an empty grey placeholder box in the feed.
			let convertedText = normalizePostWhitespace(text).replace(
				/<img\b(?![^>]*\ssrc\s*=\s*["'][^"']+["'])[^>]*>/gi,
				"",
			);
			// Collapse runs of 2+ <br> into one. The composer inserts double
			// breaks between inline images, which rendered as large uneven gaps
			// in the feed (#1384).
			convertedText = convertedText.replace(
				/(?:<br\s*\/?>\s*){2,}/gi,
				"<br>",
			);
			if (rich) {
				// Sanitize with the rich allowlist (keeps formatting) and turn
				// any mention tokens into anchors in one pass.
				convertedText = convertMentionIntoHref(convertedText, true);
			} else if (mentions.length > 0) {
				// convert the text only if the current post has at least one mention
				convertedText = convertMentionIntoHref(convertedText);
			}
			if (linked_community_documents.length > 0) {
				// convert the text only if the current post is an announcement
				convertedText = convertAnnouncementLinkIntoHref(convertedText);
			}
			return sanitizePostHtml(linkifyHtml(convertedText), rich);
		} else {
			return "";
		}
	}, [linked_community_documents.length, mentions.length, text, rich]);

	if (!text) {
		return null;
	}

	// Rich (HTML) bodies render via the browser parser to avoid the
	// react-html-replace crash on formatting markup (#1269).
	if (rich) {
		// #1428 Restore "See more / See less" for long bodies. Gate on the
		// plain-text length (not rendered height) so image-only or short posts
		// are never clamped — only genuinely long descriptions collapse.
		const plainTextLength = clean
			.replace(/<[^>]+>/g, "")
			.replace(/&nbsp;/g, " ")
			.trim().length;
		return (
			<RichPostBody html={clean} collapsible={plainTextLength > 360} />
		);
	}

	return (
		<UseReactHtmlReplace
			text={clean}
			words={words}
			linked_community_documents={linked_community_documents}
			maxHeight={maxHeight}
		/>
	);
};
